import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ApplePayIcon } from '../../../../assets/icons/apple-pay-icon.svg';
import { ReactComponent as DangerIcon } from '../../../../assets/icons/danger-icon.svg';
import { ReactComponent as MasterCardIcon } from '../../../../assets/icons/master-card-icon.svg';
import { useCurrencyText, useCurrency } from '../../../../config/branding';
import config from '../../../../config/config';
import { useBranding } from '../../../../context/BrandingContext';
import Footer from '../../../../shared-components/Footer/Footer';
import ToastMessage from '../../../../shared-components/toast-message/ToastMessage';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import getFirstSubdomain from '../../../../shared-functions/DomiansplitFunction';
import FormatTimeTo12HoursFormat from '../../../../shared-functions/FormatTime';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../../../shared-functions/LocalStorage';
import hyperPay from '../../../../utils/api/hyperPay';

// Extend the global Window interface to include wpwlOptions
declare global {
  interface Window {
    wpwlOptions?: {
      locale?: string;
      style?: string;
      /**
       * Function to handle success flow
       * @returns
       */
      onReady?: () => void;
      /**
       * Error handling function
       * @param error
       * @returns
       */
      onError?: (error: any) => void;
    };
  }
}

interface PricingDetailsProperties {
  currentPrice: string;
  dynamicPricing: {
    price: string;
    priceType: string;
    timings: {
      startDateUtc: string | null;
      endDateUtc: string | null;
    }[];
  }[];
}

interface DurationAmount {
  from: number;
  to: number;
  mode: 'grace' | 'flatRate' | 'perMinute';
  amount: number;
}

interface IdleFeePrices {
  durationAmountList: DurationAmount[];
  ceilingValue: number;
}

export type PricingType = 'FLAT_RATE' | 'PER_MINUTE';

interface PaymentMethodProperties {
  pricingDetails: PricingDetailsProperties;
  idleFeePrices: IdleFeePrices | null;
}

/**
 * get Idle FeeLabel
 * @param item
 * @returns
 */
const getIdleFeeLabel = (item: DurationAmount, t: any) => {
  const range = `(${item.from} - ${item.to} Mins)`;

  switch (item.mode) {
    case 'grace':
      return `${t('gracePeriod')} ${range}`;
    case 'flatRate':
      return `${t('flatRate')} ${range}`;
    case 'perMinute':
      return `${t('perMinute')} ${range}`;
    default:
      return '';
  }
};

const AMOUNT_REGEX = /(\d+(?:[.,]\d+)?\s*(?:SAR|AED|ريال))/i;

/**
 * Highlights currency amounts in a translation string with <strong> tags.
 * @param text - The translated string to parse
 * @returns Array of strings and React elements
 */
const highlightAmounts = (text: string): React.ReactNode[] =>
  text
    .split(AMOUNT_REGEX)
    .map((part) => (AMOUNT_REGEX.test(part) ? <strong key={part}>{part}</strong> : part));

/**
 * Function to initialize hyperPay payment flow
 * @returns
 */
function GenerateHyperPay(properties: PaymentMethodProperties) {
  const { pricingDetails, idleFeePrices } = properties;
  const { t } = useTranslation('hyperPay');
  const currency = useCurrencyText(t);
  const currencyCode = useCurrency();
  const domain = getFirstSubdomain();
  const { brandData } = useBranding();
  const [checkoutId, setCheckoutId] = useState<string>();
  const [brands, setBrands] = useState<string>('VISA MASTER MADA AMEX');
  const [buttonView, setButtonView] = useState<boolean>(true);
  const appVersion = getLocalStorageItem('appVersion') || '';

  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });

  /**
   * Function to generate register checkout Id
   */
  const generateRegistrationCheckoutId = async () => {
    try {
      const response = await hyperPay.generateCheckoutId({
        isGuestUser: true,
      });
      setBrands('VISA MASTER MADA AMEX');
      setButtonView(false);
      setLocalStorageItem('paymentMethod', 'hyperpay');
      // Set wpwlOptions before script loads
      window.wpwlOptions = {
        locale: 'en',
        style: 'card',
        /**
         * Function to handle success flow
         * @returns
         */
        onReady: () => {},
        /**
         * Function to handle error
         * @returns
         */
        onError: (error) => {
          setToastStatus({
            openToast: true,
            showToastMessage: error?.message || error?.errorMessage || 'Something went wrong',
          });
        },
      };
      setCheckoutId(response?.data?.checkoutId);
    } catch (error: any) {
      setToastStatus({
        openToast: true,
        showToastMessage: error?.message || error?.errorMessage || 'Something went wrong',
      });
    }
  };

  /**
   * Function to generate register checkout Id
   */
  const generateRegistrationApplePayCheckoutId = async () => {
    try {
      const response = await hyperPay.generateApplePayCheckoutId({
        isGuestUser: true,
      });
      setBrands('APPLEPAY');
      setButtonView(false);
      setLocalStorageItem('paymentMethod', 'applepay_via_hyperpay');
      // Set wpwlOptions before script loads
      window.wpwlOptions = {
        locale: 'en',
        style: 'card',
        /**
         * Function to handle success flow
         * @returns
         */
        onReady: () => {},
        /**
         * Function to handle error
         * @returns
         */
        onError: (error) => {
          setToastStatus({
            openToast: true,
            showToastMessage: error?.message || error?.errorMessage || 'Something went wrong',
          });
        },
      };
      setCheckoutId(response?.data?.checkoutId);
    } catch (error: any) {
      setToastStatus({
        openToast: true,
        showToastMessage: error?.message || error?.errorMessage || 'Something went wrong',
      });
    }
  };

  useEffect(() => {
    if (checkoutId) {
      const script = document.createElement('script');
      if (brands === 'APPLEPAY') {
        // script.src = `${config.hyperPayScriptUrlProd}?checkoutId=${checkoutId}`;
        script.src = `${domain && brandData?.hyperPayScriptUrl ? brandData?.hyperPayScriptUrl : config.hyperPayScriptUrl}?checkoutId=${checkoutId}`;
      } else {
        script.src = `${domain && brandData?.hyperPayScriptUrl ? brandData?.hyperPayScriptUrl : config.hyperPayScriptUrl}?checkoutId=${checkoutId}`;
      }
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }

    // Explicit return when condition is not met
    return undefined;
  }, [checkoutId]);

  return (
    <div className="hyper-pay-wrap" style={{ padding: ' 0 10px' }}>
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() =>
          setToastStatus({
            openToast: false,
            showToastMessage: '',
          })
        }
        type="error"
        message={toastStatus.showToastMessage || 'Something went wrong'}
        onCloseTimer={6000}
      />
      {buttonView && (
        <div className="payment-method-list">
          <div
            className="hyper-pay-button"
            onClick={generateRegistrationCheckoutId}
            role="presentation">
            <div className="title">{t('credit_debit_card')}</div>
            <div className="icon">
              <MasterCardIcon />
            </div>
          </div>
          <div
            className="hyper-pay-button"
            onClick={generateRegistrationApplePayCheckoutId}
            role="presentation">
            <div className="title">{t('apple_pay')}</div>
            <div className="icon">
              <ApplePayIcon />
            </div>
          </div>
        </div>
      )}
      <form action={window.location.href} className="paymentWidgets" data-brands={brands} />
      <div className="session-fee-to-show">
        <ul className="session-fee-details">
          <li>{highlightAmounts(t('sessionFeeVerification'))}</li>
          <li>{highlightAmounts(t('sessionFeePreAuth'))}</li>
          <li>{t('sessionFeeActualCost')}</li>
          {currencyCode === 'AED' && <li>{t('sessionFeeAedConversion')}</li>}
        </ul>
      </div>{' '}
      {pricingDetails.dynamicPricing.length > 0 ? (
        <div className="pricing-details-wrap">
          <p className="header">Tariff Details : </p>
          <div className="pricing-details">
            {pricingDetails.dynamicPricing.map((pricing) =>
              pricing.timings
                .filter((timing) => timing.startDateUtc !== null && timing.endDateUtc !== null)
                .map((timing) => (
                  <div
                    key={`${pricing.priceType}-${timing.startDateUtc}`}
                    className="pricing-detail">
                    <div className="pricing-content">
                      <p>
                        {FormatTimeTo12HoursFormat(ConvertUTCtoHHMM(timing.startDateUtc || ''))} -{' '}
                        {FormatTimeTo12HoursFormat(ConvertUTCtoHHMM(timing.endDateUtc || ''))}
                      </p>
                      <h6>
                        {Number(pricing.price).toFixed(2)} {currency} /KWh
                      </h6>
                    </div>
                  </div>
                )),
            )}
          </div>
        </div>
      ) : null}{' '}
      {idleFeePrices && idleFeePrices?.durationAmountList?.length > 0 && (
        <div className="idle-fee-content">
          <div className="idle-fee-card">
            <div className="header">
              <DangerIcon />
              <p>{t('idleFeeTitle')}</p>
            </div>

            <p className="description">{t('idleFeeDescription')}</p>

            {/* Idle fee slabs from API */}
            {idleFeePrices?.durationAmountList.map((item) => (
              <div className="row" key={`${item.mode}-${item.from}`}>
                <span>{getIdleFeeLabel(item, t)}</span>
                <span>-</span>
                <span>
                  {item.mode === 'grace' ? `0 ${currency}` : `${item.amount} ${currency}`}
                </span>
              </div>
            ))}

            {/* Ceiling value */}
            {idleFeePrices?.ceilingValue && (
              <div className="row">
                <span>{t('maxCeilingValue')}</span>
                <span>-</span>
                <span>
                  {idleFeePrices.ceilingValue} {currency}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={brandData?.footer ? 'app-version' : 'app-version-only'}>
        {brandData?.footer && <Footer />}
        <span>{appVersion}</span>
      </div>
    </div>
  );
}

export default GenerateHyperPay;
